import {resolveCatalogEntry, type PermissionCatalogEntry} from './permissionGrouping'
import {buildProductionUIModules} from './productionPermissionGrouping'
import type {RoleRecord} from './rolePermissionRequests'

const norm = (s: string) => s.trim().toUpperCase()

/** Action buckets aligned with product UI (permission codes unchanged). */
export type SimpleMatrixActionKey =
  | 'view'
  | 'add'
  | 'edit'
  | 'delete'
  | 'download'
  | 'workflow'
  | 'special'

export const SIMPLE_MATRIX_ACTION_ORDER: SimpleMatrixActionKey[] = [
  'view',
  'add',
  'edit',
  'delete',
  'download',
  'workflow',
  'special',
]

export const SIMPLE_MATRIX_ACTION_LABEL: Record<SimpleMatrixActionKey, string> = {
  view: 'View',
  add: 'Add',
  edit: 'Edit',
  delete: 'Delete',
  download: 'Download',
  workflow: 'Workflow',
  special: 'Special',
}

/**
 * Map a catalog row to a primary action bucket using code + catalog text only.
 */
export function classifySimpleMatrixAction(entry: PermissionCatalogEntry): SimpleMatrixActionKey {
  const r = resolveCatalogEntry(entry)
  const code = norm(r.permissionCode)
  const display = norm(r.displayName || '')
  const desc = norm(r.description || '')
  const menu = norm(r.menuLabel || '')
  const sub = norm(r.subMenuLabel || '')
  const text = `${code} ${display} ${desc} ${menu} ${sub}`
  if (!text.trim()) return 'special'

  if (/\bDELETE\b|_DELETE\b|\bDELETE_|REMOVE_|_REMOVE|\bREMOVE\b/.test(text)) return 'delete'
  if (/\bMENU_\b/.test(code) || /\bMENU_\b/.test(text)) return 'view'

  if (/\bIMPORT\b|_IMPORT|IMPORT_|\bINSERT\b|_CREATE|\bCREATE_|_ADD\b|\bADD_|NEW_|_NEW\b/.test(text)) return 'add'

  if (
    /\bDOWNLOAD\b|_EXPORT\b|\bEXPORT_|EXPORT\b|_DOWNLOAD|DOWNLOAD_/.test(text) &&
    !/\bIMPORT\b|_IMPORT|IMPORT_/.test(text)
  ) {
    return 'download'
  }

  if (
    /\bUPDATE\b|_EDIT\b|\bEDIT_|UPDATE_|MODIFY|CHANGE|WRITE_|_WRITE|MANUAL|FIELD EDIT/i.test(text) &&
    !/\bROLE_PERMISSION|\bROLE PERMISSION|\bACCESS BUNDLE|PUSH_|GRANT_/i.test(text)
  ) {
    return 'edit'
  }

  if (
    /\bAPPROVE\b|APPROVAL|SUBMIT|CLOSE|REWORK|AWARD|MARK_PAID|MARK PAID|SEND\b|SELECT\b|PUSH_|GRANT_|ASSIGN ROLE|CONFIGURE BUNDLE|WORKFLOW/i.test(text)
  ) {
    return 'workflow'
  }

  if (
    /\bMANAGE\b|ASSIGN|OVERRIDE|PERMISSION_REGISTRY|ROLE_PERMISSION|ROLE PERMISSION|ACCESS BUNDLE|BUNDLE_/i.test(text)
  ) {
    return 'workflow'
  }

  if (/\bREAD\b|_VIEW\b|\bVIEW_|READ_|LIST_|_LIST|SHOW_|_SHOW|GET_|_GET|FETCH|BROWSE|ACCESS READ|ACCESS VIEW/i.test(text))
    return 'view'

  return 'special'
}

export type SimpleMatrixModuleBucket = {
  moduleKey: string
  allCodes: string[]
  byAction: Record<SimpleMatrixActionKey, string[]>
  items: PermissionCatalogEntry[]
}

export function buildSimpleMatrixBuckets(
  catalog: PermissionCatalogEntry[],
  searchQuery: string
): SimpleMatrixModuleBucket[] {
  const modules = buildProductionUIModules(catalog, {searchQuery})
  const out: SimpleMatrixModuleBucket[] = []
  for (const mod of modules) {
    const itemsFlat = mod.sections.flatMap((s) => s.items)
    if (itemsFlat.length === 0) continue
    const byAction: Record<SimpleMatrixActionKey, string[]> = {
      view: [],
      add: [],
      edit: [],
      delete: [],
      download: [],
      workflow: [],
      special: [],
    }
    const seen = new Set<string>()
    const allCodes: string[] = []
    for (const item of itemsFlat) {
      const r = resolveCatalogEntry(item)
      const c = norm(r.permissionCode)
      if (!c || seen.has(c)) continue
      seen.add(c)
      allCodes.push(c)
      const action = classifySimpleMatrixAction(item)
      byAction[action].push(c)
    }
    if (allCodes.length === 0) continue
    out.push({moduleKey: mod.moduleLabel, allCodes, byAction, items: [...itemsFlat]})
  }
  return out
}

export type ModuleAccessBadge = 'full' | 'partial' | 'none'

export function moduleAccessLevel(
  allCodes: string[],
  selectedCodes: Set<string>
): { badge: ModuleAccessBadge; selected: number; total: number } {
  const n = allCodes.length
  if (n === 0) return {badge: 'none', selected: 0, total: 0}
  let on = 0
  for (const c of allCodes) {
    if (selectedCodes.has(norm(c))) on++
  }
  if (on === 0) return {badge: 'none', selected: 0, total: n}
  if (on === n) return {badge: 'full', selected: n, total: n}
  return {badge: 'partial', selected: on, total: n}
}

export function countModulesWithAnySelection(
  modules: SimpleMatrixModuleBucket[],
  selectedCodes: Set<string>
): number {
  let n = 0
  for (const m of modules) {
    const {selected} = moduleAccessLevel(m.allCodes, selectedCodes)
    if (selected > 0) n++
  }
  return n
}

export function buildPlainEnglishRoleSummary(
  modules: SimpleMatrixModuleBucket[],
  selectedCodes: Set<string>
): string[] {
  const lines: string[] = []
  for (const m of modules) {
    const parts: string[] = []
    for (const ak of SIMPLE_MATRIX_ACTION_ORDER) {
      const codes = m.byAction[ak]
      if (codes.length === 0) continue
      const any = codes.some((c) => selectedCodes.has(norm(c)))
      if (any) parts.push(SIMPLE_MATRIX_ACTION_LABEL[ak])
    }
    if (parts.length > 0) lines.push(`${m.moduleKey}: ${parts.join(', ')}`)
  }
  return lines
}

export function roleMatrixScopeSummary(role: RoleRecord | null): string {
  if (!role) return '—'
  if (role.systemRole) return 'System role'
  if (role.customRole && role.companyGroupAdminId != null) return 'Company-scoped role'
  if (role.customRole) return 'Global custom role'
  return 'Global role'
}
